import axios from 'axios';
import { BACKEND_URL } from '../config_and_helpers/config';
import { logError, logInfo } from '../config_and_helpers/logger';

const LOG_CONTEXT = 'product_enrichment_client';

/**
 * Flatten DRF validation errors from enrichment endpoints.
 *
 * @param {Object|Array|string|null} errPayload
 * @returns {string[]}
 */
export const formatEnrichmentErrors = errPayload => {
  if (errPayload == null) {
    return ['Request failed. Please try again.'];
  }
  if (typeof errPayload === 'string') {
    return [errPayload];
  }
  if (Array.isArray(errPayload)) {
    return errPayload.map(item => String(item));
  }
  if (errPayload.error && typeof errPayload.error === 'string') {
    return [errPayload.error];
  }
  if (errPayload.detail && typeof errPayload.detail === 'string') {
    return [errPayload.detail];
  }
  const messages = [];
  Object.entries(errPayload).forEach(([key, value]) => {
    if (value == null) return;
    if (Array.isArray(value)) {
      value.forEach(v => messages.push(`${key}: ${v}`));
    } else if (typeof value === 'object') {
      messages.push(`${key}: ${JSON.stringify(value)}`);
    } else {
      messages.push(String(value));
    }
  });
  return messages.length ? messages : ['Request failed. Please try again.'];
};

/**
 * @param {Object|null} row
 * @returns {Object|null}
 */
export const normalizeEnrichmentListRow = row => {
  if (!row) return null;
  return {
    id: row.id,
    product_id: row.product_id,
    product_cat_num: row.product_cat_num ?? '',
    product_name: row.product_name ?? '',
    supplier_name: row.supplier_name ?? '',
    status: row.status ?? '',
    matched_product_confidence: row.matched_product_confidence ?? '',
    source_url: row.source_url ?? '',
    suggested_image_url: row.suggested_image_url ?? '',
    warnings: Array.isArray(row.warnings) ? row.warnings : [],
    warning_count: row.warning_count ?? 0,
    created_at: row.created_at,
  };
};

/**
 * GET /v1/product-enrichment/pending/
 *
 * @async
 * @param {string} token
 * @param {{ nextPage?: string|null }} [options]
 * @returns {Promise<{success:boolean,results?:Object[],nextPage?:string,reachedEnd?:boolean,messages?:string[],error?:*}>}
 */
export const getPendingEnrichments = async (token, options = {}) => {
  const { nextPage = null } = options;
  const url =
    nextPage || `${BACKEND_URL}product-enrichment/pending/?page_num=1`;

  try {
    logInfo(LOG_CONTEXT, 'getPendingEnrichments start', {
      nextPageProvided: Boolean(nextPage),
    });
    const response = await axios.get(url, {
      headers: { Authorization: `Token ${token}` },
    });
    const nextCursor = response.data.next;
    const results = (response.data.results || []).map(normalizeEnrichmentListRow);

    logInfo(LOG_CONTEXT, 'getPendingEnrichments success', {
      count: results.length,
      reachedEnd: !nextCursor,
    });

    if (!nextCursor) {
      return { success: true, results, reachedEnd: true };
    }
    return { success: true, results, nextPage: nextCursor, reachedEnd: false };
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT, 'getPendingEnrichments failed', {
      status: error.response?.status,
      message: error.message,
    });
    return {
      success: false,
      error: errPayload,
      messages: formatEnrichmentErrors(errPayload),
    };
  }
};

/**
 * GET /v1/product-enrichment/{id}/
 *
 * @async
 * @param {string} token
 * @param {number|string} suggestionId
 * @returns {Promise<{success:boolean,data?:Object,messages?:string[],error?:*}>}
 */
export const getEnrichmentDetail = async (token, suggestionId) => {
  try {
    logInfo(LOG_CONTEXT, 'getEnrichmentDetail start', { suggestionId });
    const response = await axios.get(
      `${BACKEND_URL}product-enrichment/${suggestionId}/`,
      { headers: { Authorization: `Token ${token}` } }
    );
    logInfo(LOG_CONTEXT, 'getEnrichmentDetail success', { suggestionId });
    return { success: true, data: response.data };
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT, 'getEnrichmentDetail failed', {
      suggestionId,
      status: error.response?.status,
    });
    return {
      success: false,
      error: errPayload,
      messages: formatEnrichmentErrors(errPayload),
    };
  }
};

/**
 * POST /v1/product-enrichment/{id}/prepare-image-upload/
 * Returns presigned S3 POST fields for manual product image upload (Path B).
 *
 * @async
 * @param {string} token
 * @param {number|string} suggestionId
 * @param {{ file_type: string }} payload
 * @returns {Promise<{success:boolean,data?:Object,messages?:string[],error?:*}>}
 */
export const prepareEnrichmentImageUpload = async (token, suggestionId, payload) => {
  try {
    logInfo(LOG_CONTEXT, 'prepareEnrichmentImageUpload start', {
      suggestionId,
      fileType: payload?.file_type,
    });
    const response = await axios.post(
      `${BACKEND_URL}product-enrichment/${suggestionId}/prepare-image-upload/`,
      payload,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    logInfo(LOG_CONTEXT, 'prepareEnrichmentImageUpload success', {
      suggestionId,
      productImageId: response.data?.product_image_id,
    });
    return { success: true, data: response.data };
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT, 'prepareEnrichmentImageUpload failed', {
      suggestionId,
      status: error.response?.status,
    });
    return {
      success: false,
      error: errPayload,
      messages: formatEnrichmentErrors(errPayload),
    };
  }
};

/**
 * Upload a file to S3 using presigned POST data from prepare-image-upload.
 *
 * @async
 * @param {Object} prepareData - { url, fields, product_image_id }
 * @param {File} file
 * @returns {Promise<{success:boolean,product_image_id?:number,messages?:string[]}>}
 */
export const uploadPreparedEnrichmentImageToS3 = async (prepareData, file) => {
  if (!prepareData?.url || !prepareData?.fields || !file) {
    return { success: false, messages: ['Invalid image upload preparation data.'] };
  }

  try {
    const formData = new FormData();
    Object.entries(prepareData.fields).forEach(([key, value]) => {
      formData.append(key, value);
    });
    formData.append('file', file);

    logInfo(LOG_CONTEXT, 'uploadPreparedEnrichmentImageToS3 start', {
      productImageId: prepareData.product_image_id,
    });

    const response = await axios.post(prepareData.url, formData);
    if (response.status >= 200 && response.status < 300) {
      logInfo(LOG_CONTEXT, 'uploadPreparedEnrichmentImageToS3 success', {
        productImageId: prepareData.product_image_id,
      });
      return {
        success: true,
        product_image_id: prepareData.product_image_id,
      };
    }
    return { success: false, messages: ['Image upload to storage failed.'] };
  } catch (error) {
    logError(LOG_CONTEXT, 'uploadPreparedEnrichmentImageToS3 failed', {
      message: error.message,
    });
    return {
      success: false,
      messages: ['Image upload to storage failed.'],
    };
  }
};

/**
 * Prepare presigned upload and POST file to S3; returns product_image_id for submit.
 *
 * @async
 * @param {string} token
 * @param {number|string} suggestionId
 * @param {File} file
 * @returns {Promise<{success:boolean,product_image_id?:number,messages?:string[]}>}
 */
export const uploadEnrichmentImageFile = async (token, suggestionId, file) => {
  const prepareResult = await prepareEnrichmentImageUpload(token, suggestionId, {
    file_type: file.type,
  });
  if (!prepareResult.success) {
    return {
      success: false,
      messages: prepareResult.messages || ['Could not prepare image upload.'],
    };
  }
  return uploadPreparedEnrichmentImageToS3(prepareResult.data, file);
};

/**
 * POST /v1/product-enrichment/{id}/submit/
 * Primary action: apply edited form fields to the product.
 *
 * @async
 * @param {string} token
 * @param {number|string} suggestionId
 * @param {{ fields: Object }} payload
 * @returns {Promise<{success:boolean,data?:Object,messages?:string[],error?:*}>}
 */
export const submitEnrichment = async (token, suggestionId, payload) => {
  try {
    logInfo(LOG_CONTEXT, 'submitEnrichment start', {
      suggestionId,
      fieldKeys: Object.keys(payload?.fields || {}),
    });
    const response = await axios.post(
      `${BACKEND_URL}product-enrichment/${suggestionId}/submit/`,
      payload,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    logInfo(LOG_CONTEXT, 'submitEnrichment success', {
      suggestionId,
      productId: response.data?.product_id,
      appliedFields: response.data?.applied_fields,
      imageImportError: response.data?.image_import_error ?? null,
    });
    return { success: true, data: response.data };
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT, 'submitEnrichment failed', {
      suggestionId,
      status: error.response?.status,
    });
    return {
      success: false,
      error: errPayload,
      messages: formatEnrichmentErrors(errPayload),
    };
  }
};

/**
 * POST /v1/product-enrichment/{id}/approve/
 * @deprecated Use submitEnrichment with `{ fields }` instead.
 *
 * @async
 * @param {string} token
 * @param {number|string} suggestionId
 * @param {{ approved_fields: string[], edited_values?: Object }} payload
 * @returns {Promise<{success:boolean,data?:Object,messages?:string[],error?:*}>}
 */
export const approveEnrichment = async (token, suggestionId, payload) => {
  try {
    logInfo(LOG_CONTEXT, 'approveEnrichment start', {
      suggestionId,
      fieldCount: payload?.approved_fields?.length ?? 0,
    });
    const response = await axios.post(
      `${BACKEND_URL}product-enrichment/${suggestionId}/approve/`,
      payload,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    logInfo(LOG_CONTEXT, 'approveEnrichment success', {
      suggestionId,
      productId: response.data?.product_id,
    });
    return { success: true, data: response.data };
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT, 'approveEnrichment failed', {
      suggestionId,
      status: error.response?.status,
    });
    return {
      success: false,
      error: errPayload,
      messages: formatEnrichmentErrors(errPayload),
    };
  }
};

/**
 * POST /v1/product-enrichment/{id}/reject/
 *
 * @async
 * @param {string} token
 * @param {number|string} suggestionId
 * @param {{ reason?: string }} payload
 * @returns {Promise<{success:boolean,data?:Object,messages?:string[],error?:*}>}
 */
export const rejectEnrichment = async (token, suggestionId, payload = {}) => {
  try {
    logInfo(LOG_CONTEXT, 'rejectEnrichment start', { suggestionId });
    const response = await axios.post(
      `${BACKEND_URL}product-enrichment/${suggestionId}/reject/`,
      payload,
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    logInfo(LOG_CONTEXT, 'rejectEnrichment success', { suggestionId });
    return { success: true, data: response.data };
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT, 'rejectEnrichment failed', {
      suggestionId,
      status: error.response?.status,
    });
    return {
      success: false,
      error: errPayload,
      messages: formatEnrichmentErrors(errPayload),
    };
  }
};

/**
 * GET /v1/products/{id}/enrichment/latest/
 *
 * @async
 * @param {string} token
 * @param {number|string} productId
 * @returns {Promise<{success:boolean,data?:Object|null,messages?:string[],error?:*}>}
 */
export const getLatestEnrichmentForProduct = async (token, productId) => {
  try {
    logInfo(LOG_CONTEXT, 'getLatestEnrichmentForProduct start', { productId });
    const response = await axios.get(
      `${BACKEND_URL}products/${productId}/enrichment/latest/`,
      { headers: { Authorization: `Token ${token}` } }
    );
    logInfo(LOG_CONTEXT, 'getLatestEnrichmentForProduct success', {
      productId,
      suggestionId: response.data?.id,
    });
    return { success: true, data: response.data };
  } catch (error) {
    if (error.response?.status === 404) {
      logInfo(LOG_CONTEXT, 'getLatestEnrichmentForProduct none', { productId });
      return { success: true, data: null };
    }
    const errPayload = error.response?.data || error.message;
    logError(LOG_CONTEXT, 'getLatestEnrichmentForProduct failed', {
      productId,
      status: error.response?.status,
    });
    return {
      success: false,
      error: errPayload,
      messages: formatEnrichmentErrors(errPayload),
    };
  }
};

/**
 * POST /v1/product-enrichment/run-now/ (staff only)
 *
 * @async
 * @param {string} token
 * @returns {Promise<{success:boolean,data?:Object,status?:number,messages?:string[],error?:*}>}
 */
export const runEnrichmentNow = async token => {
  try {
    logInfo(LOG_CONTEXT, 'runEnrichmentNow start', {});
    const response = await axios.post(
      `${BACKEND_URL}product-enrichment/run-now/`,
      {},
      {
        headers: {
          Authorization: `Token ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
    logInfo(LOG_CONTEXT, 'runEnrichmentNow success', {
      summary: response.data,
    });
    return { success: true, data: response.data };
  } catch (error) {
    const errPayload = error.response?.data || error.message;
    const status = error.response?.status;
    logError(LOG_CONTEXT, 'runEnrichmentNow failed', { status });
    return {
      success: false,
      status,
      error: errPayload,
      messages: formatEnrichmentErrors(errPayload),
    };
  }
};
